// Analytics dashboards for the console (contract section 21): the picker list, one dashboard,
// its numbers, the star and sharing. Rep analytics shows the same dashboards as the desktop.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CallField,
  CallFieldList,
  CallFieldPatch,
  CampaignAnalyticsQuery,
  CampaignCosts,
  CampaignCounts,
  CampaignCountsQuery,
  CampaignDashboards,
  Dashboard,
  DashboardDataQuery,
  DashboardList,
  DashboardData as DashboardNumbers,
  DashboardShare,
  dashboardSpec,
  ShareTargets,
  TeamList,
} from "@vd/api-client";
import { toast } from "../components/toast";
import { ApiError, api, errorMessage } from "./client";

/** The call fields the caller can see (`GET /v1/call-fields`): labels and definitions. */
export function useCallFields() {
  return useQuery({
    queryKey: ["call-fields"],
    queryFn: async ({ signal }) => (await api<CallFieldList>("/v1/call-fields", { signal })).fields,
    staleTime: 5 * 60_000,
  });
}

/**
 * Saves a call field's label, definition, wrap-up switch or archive with the call fields API's
 * own body (`CallFieldPatch`, `PATCH /v1/call-fields/{id}`, story 8). The list refetches so the
 * precedence order and the dashboards' labels follow.
 */
export function usePatchCallField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CallFieldPatch }) =>
      api<CallField>(`/v1/call-fields/${encodeURIComponent(id)}`, { method: "PATCH", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["call-fields"] }),
  });
}

/** Calls per campaign in the caller's reach, for the scope chip. */
export function useCampaignCounts(q: CampaignCountsQuery, enabled: boolean) {
  return useQuery({
    queryKey: ["dashboards", "campaign-counts", q.range ?? "", q.from ?? "", q.to ?? ""],
    queryFn: ({ signal }) =>
      api<CampaignCounts>("/v1/dashboards/campaign-counts", { query: { ...q }, signal }),
    enabled,
    staleTime: 60_000,
  });
}

/** Whom the owner's share popover offers. */
export function useShareTargets(enabled: boolean) {
  return useQuery({
    queryKey: ["dashboards", "share-targets"],
    queryFn: ({ signal }) => api<ShareTargets>("/v1/dashboards/share-targets", { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The company's active members (`GET /v1/team`), for sharing a dashboard with people by name. */
const peopleKey = ["team", "members"] as const;
export function useSharePeople(enabled: boolean) {
  return useQuery({
    queryKey: peopleKey,
    queryFn: ({ signal }) => api<TeamList>("/v1/team", { signal }),
    enabled,
    staleTime: 60_000,
  });
}

const enc = encodeURIComponent;

export const dashboardsApi = {
  list: (signal?: AbortSignal) => api<DashboardList>("/v1/dashboards", { signal }),
  get: (id: string, signal?: AbortSignal) => api<Dashboard>(`/v1/dashboards/${enc(id)}`, { signal }),
  data: (id: string, q: DashboardDataQuery, signal?: AbortSignal) =>
    api<DashboardNumbers>(`/v1/dashboards/${enc(id)}/data`, { query: { ...q }, signal }),
  share: (id: string, body: DashboardShare) =>
    api<Dashboard>(`/v1/dashboards/${enc(id)}/share`, { method: "PUT", body }),
  patch: (id: string, body: { spec?: dashboardSpec.DashboardSpec; spec_version: number }) =>
    api<Dashboard>(`/v1/dashboards/${enc(id)}`, { method: "PATCH", body }),
  setDefault: (id: string) => api<DashboardList>("/v1/me/dashboard-default", { method: "PUT", body: { id } }),
};

export const dk = {
  list: ["dashboards", "list"] as const,
  one: (id: string) => ["dashboards", "one", id] as const,
  data: (id: string, q: DashboardDataQuery) =>
    [
      "dashboards",
      "data",
      id,
      q.range ?? "",
      q.from ?? "",
      q.to ?? "",
      q.view ?? "",
      q.rep ?? "",
      q.campaign ?? "",
      q.cost_type ?? "",
    ] as const,
};

export function useDashboardList() {
  return useQuery({
    queryKey: dk.list,
    queryFn: ({ signal }) => dashboardsApi.list(signal),
    staleTime: 60_000,
  });
}

export function useDashboard(id: string | null) {
  return useQuery({
    queryKey: dk.one(id ?? ""),
    queryFn: ({ signal }) => dashboardsApi.get(id as string, signal),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useDashboardNumbers(id: string | null, q: DashboardDataQuery) {
  return useQuery({
    queryKey: dk.data(id ?? "", q),
    queryFn: ({ signal }) => dashboardsApi.data(id as string, q, signal),
    enabled: Boolean(id),
    staleTime: 60_000,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === (id ?? "") ? prev : undefined),
  });
}

function failed(what: string) {
  return (e: unknown) => toast(`${what} didn't save. ${errorMessage(e)}`, "danger");
}

/** Someone saved the dashboard first: show theirs and say so. */
function conflicted(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409) return false;
  toast("Someone saved this dashboard first. It now shows their version.", "danger");
  return true;
}

export function useSetDefault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboardsApi.setDefault(id),
    onSuccess: (list) => qc.setQueryData(dk.list, list),
    onError: failed("Your default"),
  });
}

/** Sharing applies on click, as on the desktop. */
export function useShare(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DashboardShare) => dashboardsApi.share(id, body),
    // The choice and the people named show at once; the server's answer replaces them.
    onMutate: (body) => {
      const before = qc.getQueryData<Dashboard>(dk.one(id));
      if (before) {
        const dir = qc.getQueryData<TeamList>(peopleKey)?.members ?? [];
        const nameOf = (u: string) =>
          before.members?.find((m) => m.user_id === u)?.name ?? dir.find((m) => m.user_id === u)?.name ?? "";
        qc.setQueryData<Dashboard>(dk.one(id), {
          ...before,
          audience: body.audience,
          share_mode: body.share_mode,
          reps_see_leaderboard: body.reps_see_leaderboard ?? false,
          members:
            body.audience === "people"
              ? (body.members ?? []).map((u) => ({ user_id: u, name: nameOf(u) }))
              : [],
        });
      }
      return { before };
    },
    onSuccess: (d) => {
      qc.setQueryData(dk.one(id), d);
      void qc.invalidateQueries({ queryKey: dk.list });
      void qc.invalidateQueries({ queryKey: ["dashboards", "data", id] });
    },
    onError: (e, _b, ctx) => {
      if (ctx?.before) qc.setQueryData(dk.one(id), ctx.before);
      if (conflicted(e)) void qc.invalidateQueries({ queryKey: dk.one(id) });
      else failed("Sharing")(e);
    },
  });
}

export function usePatchDashboard(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { spec?: dashboardSpec.DashboardSpec; spec_version: number }) =>
      dashboardsApi.patch(id, body),
    onSuccess: (d) => {
      qc.setQueryData(dk.one(id), d);
      void qc.invalidateQueries({ queryKey: ["dashboards", "data", id] });
    },
    onError: (e) => {
      if (conflicted(e)) void qc.invalidateQueries({ queryKey: dk.one(id) });
      else failed("The dashboard")(e);
    },
  });
}

/** A campaign's dashboards as cards with headline numbers (`/v1/campaigns/{id}/dashboards`). */
export function useCampaignDashboards(id: string, q: CampaignAnalyticsQuery) {
  return useQuery({
    queryKey: ["dashboards", "campaign", id, "dashboards", q.range ?? "", q.from ?? "", q.to ?? ""],
    queryFn: ({ signal }) =>
      api<CampaignDashboards>(`/v1/campaigns/${enc(id)}/dashboards`, { query: { ...q }, signal }),
    staleTime: 60_000,
  });
}

/** A campaign's cost summary, managers and admins only (`/v1/campaigns/{id}/costs`). */
export function useCampaignCosts(id: string, q: CampaignAnalyticsQuery, enabled: boolean) {
  return useQuery({
    queryKey: ["dashboards", "campaign", id, "costs", q.range ?? "", q.from ?? "", q.to ?? ""],
    queryFn: ({ signal }) =>
      api<CampaignCosts>(`/v1/campaigns/${enc(id)}/costs`, { query: { ...q }, signal }),
    enabled,
    staleTime: 60_000,
  });
}
